import { ref, onMounted, onUnmounted, watch } from 'vue';
import useCompaniesQuery from './useCompaniesQuery';
import useContactsQuery from './useContactsQuery';

export default function useSearchWorker() {
  const searchResults = ref([]);
  const searchText = ref('');
  const isSearching = ref(false);
  const type = ref('all');
  let worker = null;
  let searchTimeout = null;

  // Serialize and normalize data for search
  const serializeAndNormalizeData = (data) => {
    if (!data) return [];

    return JSON.parse(JSON.stringify(data)).map((item) => {
      const normalized = {};

      // Recursively lowercase all string values
      const normalizeValue = (value) => {
        if (typeof value === 'string') {
          return value.toLowerCase();
        } else if (typeof value === 'object' && value !== null) {
          if (Array.isArray(value)) {
            return value.map(normalizeValue);
          } else {
            const normalizedObj = {};
            for (const key in value) {
              normalizedObj[key] = normalizeValue(value[key]);
            }
            return normalizedObj;
          }
        }
        return value;
      };

      for (const key in item) {
        normalized[key] = normalizeValue(item[key]);
      }

      return normalized;
    });
  };

  const loadCache = () => {
    worker.postMessage({
      companies: serializeAndNormalizeData(useCompaniesQuery.companies),
      contacts: serializeAndNormalizeData(useContactsQuery.contacts),
    });
  };

  const performSearch = (text) => {
    if (!worker) return;

    clearTimeout(searchTimeout);

    if (text.length === 0) {
      searchResults.value = [];
      return;
    }

    if (isSearching.value == true) {
      searchTimeout = setTimeout(() => {
        performSearch(text);
      }, 10);
      return;
    }

    isSearching.value = true;

    worker.postMessage({
      type: type.value,
      searchText: text,
    });
  };

  const forceUpdate = () => {
    performSearch(searchText.value);
  };

  onMounted(() => {
    worker = new Worker(new URL('../workers/searchWorker.js', import.meta.url));
    worker.onmessage = (e) => {
      // Map search results back to original data to preserve case
      const mappedResults = e.data.map((result) => {
        if (result.type === 'company') {
          const uppercaseId = result.value.toUpperCase();
          const originalCompany = useCompaniesQuery.companies.find((company) => company.COMPANYID.toUpperCase() === uppercaseId);
          if (originalCompany) {
            return {
              ...result,
              text: originalCompany.NAME,
            };
          }
        } else if (result.type === 'contact') {
          const uppercaseId = result.value.toUpperCase();
          const originalContact = useContactsQuery.contacts.find((contact) => contact.CONTACTID.toUpperCase() === uppercaseId);
          if (originalContact) {
            let fullName = originalContact.FIRSTNAME || '';
            if (originalContact.LASTNAME) {
              fullName = fullName ? `${fullName} ${originalContact.LASTNAME}` : originalContact.LASTNAME;
            }
            return {
              ...result,
              text: fullName,
            };
          }
        }
        return result;
      });

      searchResults.value = mappedResults;
      isSearching.value = false;
    };

    loadCache();

    // Initial search with empty text to populate default results
    performSearch('');
  });

  watch(searchText, (newValue) => {
    performSearch(newValue);
  });

  // Watch for changes in the companies and contacts data
  watch(useCompaniesQuery.companies, (newValue) => {
    loadCache();
  });
  watch(useContactsQuery.contacts, (newValue) => {
    loadCache();
  });

  onUnmounted(() => {
    clearTimeout(searchTimeout);
    if (worker) {
      worker.terminate();
    }
  });

  return {
    searchResults,
    searchText,
    isSearching,
    forceUpdate,
    type,
  };
}
